/** Agregacije, ki jih dovoli posamezna vrsta vira. */
export const AGREGACIJE_VIRA = {
    flow: ['ytd_flow', 'monthly_flow', 'avg_monthly_flow', 'annualized_ytd'],
    // Denarni tok in promet se bereta za leto samo (meseci 1..m).
    cash_flow: ['ytd_flow', 'avg_monthly_flow', 'annualized_ytd'],
    turnover: ['ytd_flow', 'avg_monthly_flow', 'annualized_ytd'],
    balance: ['closing_balance', 'opening_balance', 'average_month_end_balance', 'debit_turnover', 'credit_turnover'],
};
/** Agregacije, ki so stanje na dan (ne tok in ne promet). */
export const AGREGACIJE_STANJA = ['closing_balance', 'opening_balance', 'average_month_end_balance'];
export const OBDOBJA_SKLICA = ['current', 'prior_same_period', 'prior_year_end'];
/** Obišče vsa vozlišča izraza (od korena, od leve proti desni). */
export function* vozliscaIzraza(izraz) {
    yield izraz;
    switch (izraz.op) {
        case 'neg':
            yield* vozliscaIzraza(izraz.arg);
            break;
        case 'add':
        case 'sub':
        case 'mul':
        case 'div':
            yield* vozliscaIzraza(izraz.left);
            yield* vozliscaIzraza(izraz.right);
            break;
        case 'sum':
            for (const a of izraz.args)
                yield* vozliscaIzraza(a);
            break;
        default:
            break;
    }
}
/** Ključ sklica: isti vir, agregacija in kontekst sta ena sestavina. */
export function kljucSklica(s) {
    const vir = s.source.type === 'report_line'
        ? `${s.source.level}:${s.source.reference_key}`
        : s.source.type === 'semantic_role'
            ? `role:${s.source.role}`
            : `derived:${s.source.key}`;
    return `${vir}|${s.aggregation}|${s.period}`;
}
/** Sklici formule v vrstnem redu prvega pojava, brez ponovitev. */
export function skliciFormule(formula) {
    const videni = new Set();
    const sklici = [];
    for (const v of vozliscaIzraza(formula.expression)) {
        if (v.op !== 'ref')
            continue;
        const s = { source: v.source, aggregation: v.aggregation, period: v.period };
        const k = kljucSklica(s);
        if (videni.has(k))
            continue;
        videni.add(k);
        sklici.push(s);
    }
    return sklici;
}
/** Stalni ključi vrstic sheme, ki jih formula uporablja (po ravneh). */
export function kljuciFormule(formula) {
    return skliciFormule(formula).flatMap((s) => s.source.type === 'report_line' ? [{ level: s.source.level, reference_key: s.source.reference_key }] : []);
}
/**
 * Zapis formule iz baze ali datoteke. Zapisi iz prvega koraka graditelja
 * (števec in imenovalec s členi, brez časovnega konteksta) se preberejo
 * pretvorjeni v drevo izraza: A / B (× 100), vsi sklici v tekočem obdobju.
 * Zapis, ki ni ne staro ne novo, vrže napako, da kazalnik ne računa tiho
 * narobe.
 */
export function formulaIzZapisa(zapis) {
    const jeObjekt = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
    if (!jeObjekt(zapis))
        throw new Error('Formula kazalnika ni objekt.');
    if (jeObjekt(zapis.expression))
        return zapis;
    if (!Array.isArray(zapis.numerator))
        throw new Error('Formula kazalnika ni v znani obliki.');
    const stran = (cleni) => {
        const deli = cleni.map((c) => {
            const clen = c;
            const ref = { op: 'ref', source: clen.source, aggregation: clen.aggregation, period: 'current' };
            return clen.sign === -1 ? { op: 'neg', arg: ref } : ref;
        });
        return deli.length === 1 ? deli[0] : { op: 'sum', args: deli };
    };
    const a = stran(zapis.numerator);
    let izraz = a;
    if (zapis.type !== 'value' && Array.isArray(zapis.denominator)) {
        izraz = { op: 'div', left: a, right: stran(zapis.denominator) };
        if (zapis.type === 'ratio_percent')
            izraz = { op: 'mul', left: izraz, right: { op: 'num', value: '100' } };
    }
    return {
        result: 'scalar',
        expression: izraz,
        // Stara privzeta vrednost: imenovalec mora biti pozitiven.
        ...(zapis.type !== 'value' && zapis.denominator_positive !== false ? { divisor_positive: true } : {}),
        ...(zapis.hide_when_zero === true ? { hide_when_zero: true } : {}),
        display: zapis.display,
    };
}
/**
 * Veljavna koda kazalnika iz graditelja: začne se z malo črko, sledijo male
 * črke, številke ali podčrtaj, skupaj 2–50 znakov. Isto pravilo uporabljata
 * strežnik (preverjanje) in vmesnik (koda iz naziva, ročni vnos).
 */
export const VZOREC_KODE_KAZALNIKA = /^[a-z][a-z0-9_]{1,49}$/;
//# sourceMappingURL=formule.js.map